import React from 'react';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import ActivationsAPI from '../../../containers/activate/api/ActivationsAPI';
import { ActivationStatusEnum } from '../../../containers/activate/models/activation-status';
import { isLegacyActivateCurrentTeam } from '../../../core/activate-legacy-teams';
import { GlobalSearchItem, GlobalSearchRegistryItem } from '../../interfaces/global-search';
import { TARGET_TYPES } from '../../../containers/activate-target/models/target-type';
import { GenericListItemContent, GenericListItemContentProps } from './GenericListItemContent/GenericListItemContent';
import { GlobalSearchActivationData } from './interfaces/global-search-entity';
import { _activationStatusService } from '../../../core/services/activation-status.service';

export type { GlobalSearchActivationData };

export const GLOBAL_SEARCH_ACTIVATION_KEY = 'activations';

/**
 * Activations in global search follow the same 1.0/2.0 fork as the `/activation/*` route, so a
 * legacy team never sees 2.0 data behind the 1.0 UI (or vice versa) — mirrors
 * `globalSearchTargetRegistry`. Both paths emit an `/activation/{identifier}/overview` exit URL
 * whose identifier matches what the version's own detail page resolves: the primary `id` on 2.0
 * (`GET /h20/api/config/v2.0/activate/v1/pipelines/{id}` is keyed on it), the team-scoped `seq_id` on 1.0
 * (`/api/config/activations/{seqId}/details`).
 *
 * Resolved per call rather than at module load, so a fresh login is picked up without a reload. The
 * gate itself pins the version for the session, which is what keeps these two identifier schemes
 * from crossing: a mid-session flag flip used to leave this registry emitting primary ids into a
 * still-mounted 1.0 app that reads them as seq_ids.
 */
export const GlobalSearchActivationRegistry: GlobalSearchRegistryItem<GlobalSearchActivationData> = {
  key: GLOBAL_SEARCH_ACTIVATION_KEY,
  title: 'Activations',
  sortOrder: 4,
  maxItemsWhenNoFiltersApplied: 2,
  doNotRender: () => !_activationStatusService?.shouldShowActivation(),
  filters: [
    {
      value: GLOBAL_SEARCH_ACTIVATION_KEY,
      label: 'Activations'
    }
  ],
  viewAllRoute: '/activation',
  inMemoryFiltering: true,
  dataResolver: () =>
    isLegacyActivateCurrentTeam() ? legacyActivationsResolver() : activate2ActivationsResolver(),
  /*
    Shared by both versions: the item `data` shape is version-agnostic. The brand colours come from
    the static 1.0 catalogs, which are keyed by the same uppercase type strings 2.0 uses — a 2.0 type
    the catalogs predate simply resolves to `undefined` and renders without a colour chip, as on the
    2.0 target path.
  */
  renderItem: (item) => {
    const props: GenericListItemContentProps = {
      suffixIcon: item.icon,
      // The activation's own name (`item.name`), which both factories set — reading it off
      // `data.warehouse.name` worked only because the 1.0 factory stored the activation name there.
      primaryText: item.name,
      primaryLogoTooltip: item.data.warehouse.displayType,
      primaryLogoProps: {
        logoURL: item.data.warehouse.logoUrl,
        darkModeLogoURL: item.data.warehouse.darkModeLogoUrl,
        primaryColor: DESTINATION_TYPES[item.data.warehouse.type]?.primaryColor,
        darkModePrimaryColor: DESTINATION_TYPES[item.data.warehouse.type]?.darkModePrimaryColor
      },
      logoConnector: true,
      logoConnectorColor: item.data.status,
      secondaryText: item.data.target.name,
      secondaryLogoProps: {
        logoURL: item.data.target.logoUrl,
        darkModeLogoURL: item.data.target.darkModeLogoUrl,
        primaryColor: TARGET_TYPES[item.data.target.type]?.primaryColor,
        darkModePrimaryColor: TARGET_TYPES[item.data.target.type]?.darkModePrimaryColor
      },
      secondaryLogoTooltip: item.data.target.displayType
    };

    return (
      <GenericListItemContent {...props} />
    );
  }
};

/** Activate 1.0 (in-ETL reverse-ETL under `/api/config`) — unchanged from pre-migration behaviour. */
function legacyActivationsResolver() {
  return ActivationsAPI.getActivations().then(res => {
    const activations = res.data.map(GlobalSearchActivationFactory);
    return [...activations];
  });
}

/**
 * Activate 2.0 (standalone activate-service) — the same list the Activations tab renders.
 *
 * Reached through a dynamic `import()` for the reason given on the Targets registry's own 2.0
 * resolver: this module is eager, so a static import of the 2.0 service and factory would put them
 * in an eager chunk and undo the lazy boundary `DashboardRoutes` establishes for the 2.0 route tree.
 */
function activate2ActivationsResolver() {
  return import('../../../activate/activations/global-search/activate2ActivationsResolver').then(module =>
    module.activate2ActivationsResolver()
  );
}

export function GlobalSearchActivationFactory(
  rawActivation: any
): GlobalSearchItem<GlobalSearchActivationData> {
  let status = ActivationStatusEnum[rawActivation.status]?.colorType;

  if (rawActivation.status === 'OK') {
    status = ActivationStatusEnum[rawActivation.state].colorType;
  }

  return {
    data: {
      warehouse: {
        name: rawActivation.name,
        type: rawActivation.warehouse.type,
        displayType: rawActivation.warehouse.type_display_name,
        logoUrl: rawActivation.warehouse.type_logo_url,
        darkModeLogoUrl:
          rawActivation.warehouse.type_dark_mode_logo_url ?? rawActivation.warehouse.type_logo_url
      },
      target: {
        name: rawActivation.target.name,
        type: rawActivation.target.type,
        displayType: rawActivation.target.type_display_name,
        logoUrl: rawActivation.target.type_logo_url,
        darkModeLogoUrl: rawActivation.target.type_dark_mode_logo_url ?? rawActivation.target.type_logo_url,
      },
      status,
    },
    key: `${rawActivation.warehouse.type}-activation-${rawActivation.seq_id}`,
    name: rawActivation.name,
    exitURL: `/activation/${rawActivation.seq_id}/overview`,
    icon: 'activate',
    searchKeywords: [
      rawActivation.name,
      rawActivation.warehouse.type,
      rawActivation.warehouse.type_display_name
    ]
  };
}

/*
 * `GlobalSearchActivate2ActivationFactory` moved to
 * `activate/activations/global-search/activate2ActivationsResolver` — it maps a 2.0 `Activation`, so
 * keeping it here would reintroduce the static import of the 2.0 interface this module now avoids.
 */
